import { useBusinessContext } from "../BusinessContext";
import { personScopeUrl } from "./personScope";
import { personDisplayName } from "./personName";
import { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, MenuItem } from "@mui/material";
import DatePicker from "./DialogDatePicker";
import { normalizeDialogDate } from "./dialogDate";
import dayjs from "dayjs";
import api from "../api/axios";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";
import LoadingIndicator from './LoadingIndicator';
import LoadError from './LoadError';

function VerkaufDialog({ open, onClose, onSave, verkauf, streckenListe = [] }) {
  const [streckeId, setStreckeId] = useState("");
  const { mandantId, betriebId } = useBusinessContext();
  const [kundeId, setKundeId] = useState("");
  const [datum, setDatum] = useState(dayjs());
  const [menge, setMenge] = useState("");
  const [einheit, setEinheit] = useState("");
  const [preis, setPreis] = useState("");
  const [betrag, setBetrag] = useState("");
  const [bezahlt, setBezahlt] = useState(false);
  const [kundenListe, setKundenListe] = useState([]);
  const [loading, setLoading] = useState(false), [loadError, setLoadError] = useState(''), [attempt, setAttempt] = useState(0);

  // Felder zurücksetzen oder mit Werten befüllen beim Öffnen
  useEffect(() => {
    if (open) {
      if (verkauf) {
        setStreckeId(verkauf.Strecke_id || "");
        setKundeId(verkauf.Kunde_id || "");
        setDatum(verkauf.Datum ? dayjs(verkauf.datum) : dayjs());
        setMenge(verkauf.Menge || "");
        setEinheit(verkauf.Einheit || "");
        setPreis(verkauf.Preis || "");
        setBetrag(verkauf.Betrag || "");
        setBezahlt(Boolean(verkauf.Bezahlt));
      } else {
        setStreckeId("");
        setKundeId("");
        setDatum(dayjs());
        setMenge("");
        setEinheit("");
        setPreis("");
        setBetrag("");
        setBezahlt(false);
      }
    }
  }, [open, verkauf]);

  // Kundenliste von der API laden
  useEffect(() => {
    let active = true;
    if (!open || !mandantId || !betriebId) return;
    setLoading(true); setLoadError(''); setKundenListe([]);
    api.get(personScopeUrl("/personen?filter=kunden", mandantId, betriebId))
      .then(res => {
        if (active) setKundenListe(res.data);
        // console.log("Kundenliste geladen:", res.data);
      })
      .catch(() => { if (active) setLoadError('Kunden konnten nicht geladen werden.'); })
      .finally(() => { if (active) setLoading(false); });
      // console.log("Kundenliste geladen:", kundenListe);
    return () => { active = false; };
  }, [open, mandantId, betriebId, attempt]);

  const handleSave = () => {
    onSave({
      strecke_id: streckeId,
      kunde_id: kundeId,
      datum: datum ? normalizeDialogDate(dayjs(datum)).format("YYYY-MM-DD") : "",
      menge,
      einheit,
      preis,
      betrag,
      bezahlt: bezahlt ? 1 : 0
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verkaufsposition erfassen</DialogTitle>
      <DialogContent>
        {loading && <LoadingIndicator label="Kunden werden geladen …" />}
        {loadError && <LoadError onRetry={() => setAttempt(value => value + 1)}>{loadError}</LoadError>}
        <Grid container spacing={2}>
          {/* Streckeneintrag */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Streckeneintrag"
              value={streckeId}
              onChange={e => setStreckeId(e.target.value)}
              margin="dense"
            >
              {streckenListe.map((strecke) => (
                <MenuItem key={strecke.Id} value={strecke.Id}>
                  {strecke.Datum} – {strecke.Wildname} ({strecke.altersklasse})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Kunde */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Kunde"
              value={kundeId}
              onChange={e => setKundeId(e.target.value)}
              margin="dense"
            >
              {kundenListe.map((kunde) => (
                <MenuItem key={kunde.Id} value={kunde.Id}>
                  {personDisplayName(kunde)} ({kunde.Id})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Datum */}
          <Grid item xs={6}>
            <DatePicker
              label="Verkaufsdatum"
              value={datum}
              onChange={newValue => setDatum(newValue)}
              format="YYYY-MM-DD"
              renderInput={params => <TextField {...params} fullWidth margin="dense" />}
            />
          </Grid>
          {/* Menge */}
          <Grid item xs={6}>
            <TextField
              sx={{ width: { xs: "100%", sm: "10ch" } }}
              label="Menge"
              margin="dense"
              value={menge}
              onChange={e => setMenge(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: "any" }}
            />
          </Grid>
          {/* Einheit */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Einheit"
              margin="dense"
              value={einheit}
              onChange={e => setEinheit(e.target.value)}
            />
          </Grid>
          {/* Preis */}
          <Grid item xs={6}>
            <TextField
              sx={{ width: { xs: "100%", sm: "12ch" } }}
              label="Preis (€)"
              margin="dense"
              value={preis}
              onChange={e => setPreis(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: "any" }}
            />
          </Grid>
          {/* Betrag */}
          <Grid item xs={6}>
            <TextField
              sx={{ width: { xs: "100%", sm: "12ch" } }}
              label="Betrag (€)"
              margin="dense"
              value={betrag}
              onChange={e => setBetrag(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: "any" }}
            />
          </Grid>
          {/* Bezahlt */}
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Bezahlt"
              margin="dense"
              value={bezahlt ? "1" : "0"}
              onChange={e => setBezahlt(e.target.value === "1")}
            >
              <MenuItem value="1">Ja</MenuItem>
              <MenuItem value="0">Nein</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" startIcon={<Close />}>Abbrechen</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={<Save />}
          disabled={!streckeId || !kundeId || !datum}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VerkaufDialog;
